import React from "react";
import {AdministrationService} from "../../services/administrationService";
import i18n from "../../../i18n/i18n";
import {Chip} from "@mui/material";
import Loader from "../../../common/components/shared/loader/loader";
import Table from "../../../common/components/shared/table/table";
import {Api} from "../../../helpers/api";
import {UserInManagementModel} from "../../models/user";
import "./usersList.scss";
import DisablePopup from "./disablePopup/disablePopup";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import EnablePopup from "./enablePopup/enablePopup";
import {NavLink} from "react-router-dom";
import {routingAddresses} from "../../../helpers/routing";
import {withRouter} from "react-router";
import Button from "../../../common/components/shared/button/button";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import ResetPasswordPopup from "../resetPasswordPopup/resetPasswordPopup";
import {storesContext} from "../../../common/stores/stores";
import {IUserStore} from "../../stores/userStore";
import ReloadButton from "../../../common/components/shared/reloadButton/reloadButton";
import ChangeUserPasswordPopup from "../changeUserPasswordPopup/changeUserPasswordPopup";
import {SortDirection} from "../../../common/models/table";
import RemovePopup from "../../../common/components/shared/removePopup/removePopup";

interface UsersListState {
    users: UserInManagementModel[] | undefined;
    usersResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedElement: UserInManagementModel | undefined;
    displayPopup: "enablePopup" | "disablePopup" | "resetPasswordPopup" | "changePasswordPopup" | "removePopup" | null;
    disableUserResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    enableUserResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    resetPasswordResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    removeUserResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    orderBy: string | undefined;
    sortDirection: SortDirection | undefined;
    isTableLoader: boolean;
    numberOfElements: number;
    isPopupActionWaiting: boolean;
}

class UsersList extends React.Component <any, UsersListState> {
    static contextType = storesContext;

    private sortableElements = ["enabled", "username", "role", "language"];

    constructor(props: any) {
        super(props);

        this.state = {
            users: undefined,
            usersResponse: undefined,
            selectedElement: undefined,
            displayPopup: null,
            disableUserResponse: undefined,
            enableUserResponse: undefined,
            resetPasswordResponse: undefined,
            removeUserResponse: undefined,
            currentPage: 0,
            orderBy: undefined,
            sortDirection: undefined,
            isTableLoader: false,
            numberOfElements: 0,
            isPopupActionWaiting: false
        };
    }

    public async componentDidMount() {
        await this.getAllUsers(false);
    }

    public render(): JSX.Element {
        const properties = ["enabled", "username", "role", "language"];
        const headers = properties.map(key => i18n.t("administration.headers." + key));
        const userStore = this.context.userStore as IUserStore;

        return (
            <div className="users-list">
                {this.renderWrapperContent(headers, properties)}
                {userStore.canChangeOrResetPassword &&  (
                    <>
                        {this.renderPopupForChangePassword()}
                        {this.renderPopupForResetPassword()}
                    </>
                )}
                {userStore.canModifyUser && (
                    <>
                        {this.renderPopupsForModifyUser()}
                        {this.renderPopupRemoveUser()}
                    </>
                )}
            </div>
        );
    }

    private renderHeader() {
        const userStore = this.context.userStore as IUserStore;

        return (
            <div className="users-header">
                {userStore.canModifyUser && (
                    <NavLink to={routingAddresses.administration.add} data-testid="add-user">
                        <Button type="primary" text={i18n.t("administration.actions.addUser")}/>
                    </NavLink>
                )}
                <ReloadButton
                    reloadFunc={this.reload.bind(this)}
                    testId="user-list-reload-btn"
                />
            </div>
        );
    }

    private renderWrapperContent(headers: string[], properties: string[]): JSX.Element {
        if (!Api.isSuccess(this.state.usersResponse)) {
            return (
                <HttpError
                    response={this.state.usersResponse}
                    cause={i18n.t("administration.itemRetrievalErrorSuffix")}
                />
            );
        } else if (this.state.users?.length) {
            return (
                <>
                    {this.renderHeader()}
                    <div className="users-content">
                        <Table
                            headers={headers}
                            rows={this.state.users}
                            properties={properties}
                            testId={"users-table"}
                            actions={this.renderActions.bind(this)}
                            renderCell={this.renderCell}
                            currentPage={this.state.currentPage}
                            numberOfElements={this.state.numberOfElements}
                            isTableLoader={this.state.isTableLoader}
                            sortableElements={this.sortableElements}
                            sortingAction={this.sortingAction.bind(this)}
                            orderBy={this.state.orderBy}
                            sortDirection={this.state.sortDirection}
                            pageable
                            isOldPagination
                        />
                    </div>
                </>
            );
        } else {
            return (
                <Loader/>
            );
        }
    }

    private async sortingAction(orderBy: string, sortDirection: SortDirection) {
        await this.setState({orderBy, sortDirection});
        await this.getAllUsers(false);
    }

    private renderCell(row: any, property: string): JSX.Element {
        if (property === "enabled") {
            const status = row[property] == true ? "enabled" : "disabled";
            return <Chip className={`${status}-chip`} label={i18n.t(`administration.status.${status}`)}/>;
        } else if (property === "language" && row[property]) {
            return <>{i18n.t(`administration.languages.${row[property]}`)}</>;
        } else if (property === "role" && row[property]) {
            return <>{i18n.t(`administration.roles.${(row[property]).toLocaleLowerCase()}`)}</>;
        }
        return row[property];
    }

    private renderPopupsForModifyUser(){
        return (
            <>
                <DisablePopup
                    displayPopup={this.state.displayPopup === "disablePopup"}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirm.bind(this, "disablePopup", false)}
                    username={this.state.selectedElement?.username || ""}
                    response={this.state.disableUserResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
                <EnablePopup
                    displayPopup={this.state.displayPopup === "enablePopup"}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirm.bind(this, "enablePopup", true)}
                    username={this.state.selectedElement?.username || ""}
                    response={this.state.enableUserResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
            </>
        );
    }

    private renderPopupForResetPassword(){
        return (
            <ResetPasswordPopup
                displayPopup={this.state.displayPopup === "resetPasswordPopup"}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirm.bind(this, "resetPasswordPopup", false)}
                username={this.state.selectedElement?.username || ""}
                response={this.state.resetPasswordResponse}
                isWaiting={this.state.isPopupActionWaiting}
            />
        );
    }

    private renderPopupForChangePassword(){
        return (
            <ChangeUserPasswordPopup
                displayPopup={this.state.displayPopup === "changePasswordPopup"}
                closePopup={this.closePopup.bind(this)}
                username={this.state.selectedElement?.username || ""}
                userId={this.state.selectedElement ? this.state.selectedElement!.id : ""}
                confirmSuccessAction={this.reload.bind(this)}
            />
        );
    }

    private renderPopupRemoveUser() {
        const content = <div>
            {i18n.t("administration.removeAction.firstPart")}
            <b>{this.state.selectedElement?.username || ""}</b>
            {i18n.t("administration.removeAction.lastPart")}
        </div>;

        return (
            <RemovePopup
                displayPopup={this.state.displayPopup === "removePopup"}
                content={content}
                header={i18n.t("administration.removeAction.header")}
                secondHeader={i18n.t("administration.removeAction.secondHeader")}
                actionText={i18n.t("administration.removeAction.actionText")}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirm.bind(this, "removePopup", false)}
                response={this.state.removeUserResponse}
                isWaiting={this.state.isPopupActionWaiting}
                httpErrorMessage={i18n.t("administration.removeAction.errorMessage")}
            />
        );
    }

    private renderActions(user: UserInManagementModel) {
        const userId = user.id;
        const userStore = this.context.userStore as IUserStore;

        if (!userId) {
            return <></>;
        }

        return (
            <>
                {userStore.canChangeOrResetPassword && (
                    <>
                        {this.renderChangePasswordAction(user, userId)}
                        {this.renderResetPasswordAction(user, userId)}
                    </>
                )}
                {userStore.canModifyUser && (
                    <>
                        {this.renderModifyAction(userId)}
                        {this.renderEnableOrDisableActions(user, userId)}
                        {this.renderRemoveActions(user, userId)}
                    </>
                )}
            </>
        );
    }

    private renderResetPasswordAction(user: UserInManagementModel, userId: string) {
        return (
            <span className="reset-password-button" data-testid={`reset-password-button-${userId}`} onClick={() => {
                this.setState({selectedElement: user});
                this.onResetOrChangePasswordAction("resetPasswordPopup");
            }}>
                {i18n.t("administration.actions.resetPassword")}
            </span>
        );
    }

    private renderChangePasswordAction(user: UserInManagementModel, userId: string) {
        return (
            <span className="change-password-button" data-testid={`change-password-button-${userId}`} onClick={() => {
                this.setState({selectedElement: user});
                this.onResetOrChangePasswordAction("changePasswordPopup");
            }}>
                {i18n.t("administration.actions.changePassword")}
            </span>
        );
    }

    private renderModifyAction(userId: string) {
        return (
            <NavLink
                to={`${routingAddresses.administration.modify}/${userId}`}
                className="modify-button"
                data-testid={`modify-${userId}`}
            >
                {i18n.t("modify")}
            </NavLink>
        );
    }

    private renderEnableOrDisableActions(user: UserInManagementModel, userId: string) {
        return (
            <>
                {user.enabled ? (
                    <span className="disable-button" data-testid={`disable-button-${userId}`} onClick={() => {
                        this.setState({selectedElement: user});
                        this.onDisableAction();
                    }}>
                        {i18n.t("administration.actions.disable")}
                    </span>
                ) : (
                    <span className="enable-button" data-testid={`enable-button-${userId}`} onClick={() => {
                        this.setState({selectedElement: user});
                        this.onEnableAction();
                    }}>
                        {i18n.t("administration.actions.enable")}
                    </span>
                )}
            </>
        );
    }

    private renderRemoveActions(user: UserInManagementModel, userId: string) {
        return (
            <span className="remove-button" data-testid={`remove-button-${userId}`} onClick={() => {
                this.setState({selectedElement: user});
                this.onRemoveAction();
            }}>
                {i18n.t("remove")}
            </span>
        );
    }

    private onDisableAction() {
        this.setState({displayPopup: "disablePopup"});
    }

    private onEnableAction() {
        this.setState({displayPopup: "enablePopup"});
    }

    private onResetOrChangePasswordAction(disablePopup: "resetPasswordPopup" | "changePasswordPopup") {
        this.setState({displayPopup: disablePopup});
    }

    private onRemoveAction() {
        this.setState({displayPopup: "removePopup"});
    }

    private closePopup() {
        this.setState({
            displayPopup: null,
            enableUserResponse: undefined,
            disableUserResponse: undefined,
            resetPasswordResponse: undefined,
            removeUserResponse: undefined
        });
    }

    private async confirm(popupType: "enablePopup" | "disablePopup" | "resetPasswordPopup" | "removePopup", enable: boolean) {
        this.setState({isPopupActionWaiting: true, usersResponse: undefined});
        let response = undefined;

        if (popupType === "enablePopup" || popupType === "disablePopup") {
            this.setState({
                enableUserResponse: undefined,
                disableUserResponse: undefined
            });

            response = await AdministrationService.enableOrDisableUser(this.state.selectedElement!.id, enable);

            if (enable) {
                this.setState({enableUserResponse: response});
            } else {
                this.setState({disableUserResponse: response});
            }
        } else if (popupType === "resetPasswordPopup") {
            this.setState({resetPasswordResponse: undefined});

            response = await AdministrationService.resetUserPassword(this.state.selectedElement!.id);

            this.setState({
                resetPasswordResponse: response
            });
        } else if (popupType === "removePopup") {
            this.setState({removeUserResponse: undefined});

            response = await AdministrationService.removeUser(this.state.selectedElement!.id);

            this.setState({
                removeUserResponse: response
            });
        }

        if (Api.isSuccess(response)) {
            await this.getAllUsers(true);

            this.setState({
                selectedElement: undefined,
            });

            this.closePopup();
        }

        this.setState({isPopupActionWaiting: false});
    }

    private async getAllUsers(withoutTableLoader: boolean) {
        this.setState({usersResponse: undefined, isTableLoader: withoutTableLoader ? false : true});

        const response = await AdministrationService.getAll(
            {
                orderBy: this.state.orderBy,
                sortDirection: this.state.sortDirection
            }
        );
        this.setState({
            users: response?.data?.data || null,
            usersResponse: response,
            currentPage: response?.data?.currentPage,
            numberOfElements: response?.data?.numberOfElements,
            isTableLoader: false
        });
    }

    private async reload() {
        this.setState({users: undefined});
        await this.getAllUsers(false);
    }
}

export default withRouter(UsersList);
